import { useEffect, useMemo, useState, useRef } from 'react';
import { createRoute, redirect, useParams } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Route as rootRoute } from '../../__root';
import { useLiveStreamsStore } from '@/client/store/liveStreamsStore';
import { useClientsStore } from '@/client/store/clientsStore';
import { API_BASE_URL } from '@/client/config';
import { generateStreamingURIs, formatDuration } from '@/client/lib/format';
import { StreamDetailView, StreamDetailData } from '@/client/components/stream-detail-view';
import type { LiveStreamTableRow, LiveStreamWithEventResponse } from '@shared/types';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'events/live-streams/$streamId',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: LiveStreamDetailComponent,
});

function LiveStreamDetailComponent() {
  // streamId is in format: clientID_eventID
  const { streamId } = useParams({ from: '/events/live-streams/$streamId' });
  const { getAccessTokenSilently, logout } = useAuth0();

  // Parse clientID and eventID from streamId
  const [clientID, eventID] = useMemo(() => {
    const parts = streamId.split('_');
    if (parts.length >= 2) {
      return [parts[0], parts.slice(1).join('_')];
    }
    return [null, streamId];
  }, [streamId]);

  // Store state
  const liveEvents = useLiveStreamsStore((state) => state.liveEvents);
  const completedEvents = useLiveStreamsStore((state) => state.completedEvents);
  const liveStreams = useLiveStreamsStore((state) => state.liveStreams);
  const loading = useLiveStreamsStore((state) => state.loading);
  const fetchLiveStreams = useLiveStreamsStore((state) => state.fetchLiveStreams);

  // Client name and timezone lookup
  const clients = useClientsStore((state) => state.clients);
  const { clientName, clientTimeZone } = useMemo(() => {
    if (!clientID) return { clientName: null, clientTimeZone: null };
    const client = clients.find((c) => c.clientID === clientID);
    return {
      clientName: client?.name || null,
      clientTimeZone: client?.timeZone || null,
    };
  }, [clientID, clients]);

  // Infrastructure data fetched from API (for WebSocket-sourced streams)
  const [infraData, setInfraData] = useState<LiveStreamWithEventResponse | null>(null);
  const infraFetchedForRef = useRef<string | null>(null);

  // Fetch live streams if store is empty (handles direct navigation / refresh)
  useEffect(() => {
    const hasData = liveEvents.length > 0 || liveStreams.length > 0;
    if (!hasData && !loading) {
      getAccessTokenSilently().then((token) => {
        fetchLiveStreams(token, logout);
      });
    }
  }, []);

  // Find stream by matching against parsed IDs
  // Returns { stream, isFromWebSocket } to track data source
  const { stream, isFromWebSocket } = useMemo((): { stream: LiveStreamTableRow | null; isFromWebSocket: boolean } => {
    // Check WebSocket live events first
    for (const event of liveEvents) {
      if (event.clientID === clientID && event.eventID === eventID) {
        const wsStream = transformWsEvent(event, clientName, clientTimeZone);
        // Merge with infrastructure data if available
        if (infraData) {
          return {
            stream: {
              ...wsStream,
              liveID: infraData.liveID || wsStream.liveID,
              ingInstanceID: infraData.ingInstanceID,
              egInstanceID: infraData.egInstanceID,
              wcStreamID: infraData.wcStreamID,
            },
            isFromWebSocket: true,
          };
        }
        return { stream: wsStream, isFromWebSocket: true };
      }
    }

    // Check completed events
    for (const event of completedEvents) {
      if (event.clientID === clientID && event.eventID === eventID) {
        const wsStream = transformWsEvent(event, clientName, clientTimeZone);
        if (infraData) {
          return {
            stream: {
              ...wsStream,
              liveID: infraData.liveID || wsStream.liveID,
              ingInstanceID: infraData.ingInstanceID,
              egInstanceID: infraData.egInstanceID,
              wcStreamID: infraData.wcStreamID,
            },
            isFromWebSocket: true,
          };
        }
        return { stream: wsStream, isFromWebSocket: true };
      }
    }

    // Check API live streams (eventID is already composite: clientID_eventID)
    for (const s of liveStreams) {
      if (s.eventID === streamId) {
        // Transform API response to full table row format
        return {
          stream: {
            ...s,
            streamingURIs: generateStreamingURIs(clientID, eventID),
            private: s.private,
            liveToBreak: s.liveToBreak,
            liveStats: null,
          },
          isFromWebSocket: false,
        };
      }
    }

    return { stream: null, isFromWebSocket: false };
  }, [liveEvents, completedEvents, liveStreams, clientID, eventID, streamId, clientName, clientTimeZone, infraData]);

  // Fetch infrastructure data from API when viewing WebSocket-sourced stream
  // This fills in liveID, ingInstanceID, egInstanceID, wcStreamID
  useEffect(() => {
    // Only fetch if:
    // 1. We have a stream from WebSocket
    // 2. We haven't already fetched for this specific stream
    // 3. Stream is in a live state (infrastructure exists)
    const isLiveStatus = stream?.status && ['starting', 'live', 'on break', 'stopping'].includes(stream.status);

    if (isFromWebSocket && stream && isLiveStatus && infraFetchedForRef.current !== streamId) {
      infraFetchedForRef.current = streamId;

      getAccessTokenSilently().then(async (token) => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/live-streams`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const data = await response.json();
            // Find the matching stream in API response
            const apiStream = data.liveStreams?.find(
              (s: LiveStreamWithEventResponse) => s.eventID === streamId
            );
            if (apiStream) {
              setInfraData(apiStream);
            }
          }
        } catch (err) {
          console.error('Error fetching infrastructure data:', err);
        }
      });
    }
  }, [isFromWebSocket, stream, streamId, getAccessTokenSilently]);

  // Transform WebSocket event to table row format
  function transformWsEvent(event: any, clientNameOverride: string | null, clientTimeZoneOverride: string | null): LiveStreamTableRow {
    return {
      clientID: event.clientID,
      clientName: clientNameOverride,
      clientTimeZone: clientTimeZoneOverride,
      eventID: event.eventID,
      eventTitle: event.title,
      liveID: event.liveStats?.liveID || null,
      status: event.eventStatus,
      streamStatus: event.liveStats?.status || null,
      eventStartDateTime: event.startDateTime,
      eventEndDateTime: null,
      startDateTime: event.liveStats?.startDate || null,
      endDateTime: null,
      autoStart: event.autoStart,
      autoStop: event.autoStop,
      preroll: event.preroll,
      encoderID: event.assignedEncoder,
      ingInstanceID: null,
      egInstanceID: null,
      wcStreamID: null,
      streamingURIs: event.streamingURIs
        ? { main: event.streamingURIs.main || null, backup: event.streamingURIs.backup || null }
        : null,
      customID: event.customID,
      description: event.description,
      notes: event.notes,
      private: event.private,
      liveToBreak: event.liveToBreak,
      liveStats: event.liveStats
        ? { runtime: event.liveStats.runtime, status: event.liveStats.status }
        : null,
    };
  }

  // Calculate runtime - prefer liveStats, fall back to calculated from startDateTime
  const runtimeValue = useMemo(() => {
    if (stream?.liveStats?.runtime) return stream.liveStats.runtime;
    if (!stream?.startDateTime) return null;
    const elapsedSeconds = Math.floor((Date.now() - new Date(stream.startDateTime).getTime()) / 1000);
    return elapsedSeconds > 0 ? formatDuration(elapsedSeconds) : null;
  }, [stream?.liveStats?.runtime, stream?.startDateTime]);

  // Get stream status - prefer liveStats, fall back to API streamStatus
  const streamStatusValue = stream?.liveStats?.status || stream?.streamStatus || null;

  // Handle refresh
  const handleRefresh = async () => {
    const token = await getAccessTokenSilently();
    await fetchLiveStreams(token, logout);
  };

  // Convert LiveStreamTableRow to StreamDetailData
  const streamDetailData: StreamDetailData | null = stream ? {
    clientID: stream.clientID,
    clientName: stream.clientName,
    eventID: stream.eventID,
    eventTitle: stream.eventTitle,
    status: stream.status,
    streamStatus: stream.streamStatus,
    eventStartDateTime: stream.eventStartDateTime,
    eventEndDateTime: stream.eventEndDateTime,
    startDateTime: stream.startDateTime,
    endDateTime: stream.endDateTime,
    autoStart: stream.autoStart,
    autoStop: stream.autoStop,
    preroll: stream.preroll,
    customID: stream.customID,
    description: stream.description,
    notes: stream.notes,
    private: stream.private,
    liveToBreak: stream.liveToBreak,
    encoderID: stream.encoderID,
    liveID: stream.liveID,
    ingInstanceID: stream.ingInstanceID,
    egInstanceID: stream.egInstanceID,
    wcStreamID: stream.wcStreamID,
    streamingURIs: stream.streamingURIs,
    liveStats: stream.liveStats,
  } : null;

  return (
    <StreamDetailView
      stream={streamDetailData}
      loading={loading && !stream}
      backTo="/events/live-streams"
      backLabel="Back to Live Streams"
      onRefresh={handleRefresh}
      streamId={streamId}
      runtimeValue={runtimeValue}
      streamStatusValue={streamStatusValue}
      isLiveStream={true}
    />
  );
}
